import { api, ApiError } from '../api/client';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { bindPhoneInputNormalize, isValidE164Phone, normalizeToE164 } from '../crypto/smsPassword';
import {
  buildSmsDestinationCiphertext,
  createSmsDeliveryReference,
  revealSmsDestination,
} from '../crypto/smsDeliveryClient';
import { loadVaultSession } from '../crypto/vaultSession';
import { iconImg } from '../ui/icons';
import { buildSmsStatusFlow, type SmsFlowStatus } from '../ui/smsStatusFlow';
import { createHelpTip } from '../help/helpTips';
import {
  connectSmsDeliveryLive,
  disconnectSmsDeliveryLive,
  isSmsDeliveryLiveActive,
  type SmsDeliveryUpdatedMessage,
} from '../ws/smsDeliveryLive';
import { catchUpSmsDeliveryStatus } from '../ws/smsDeliveryCatchUp';
import { buildSmsDevSimulateControls, loadUsesDevLogSink } from '../ui/smsDevSimulateControls';

interface SessionSmsRow {
  reference: string;
  phone: string;
  status: SmsFlowStatus;
  errorDescription: string | null;
  sentAt: number;
  /** Timeout while waiting for a terminal DLR. */
  pendingTimeoutId: number | null;
  /** Auto-remove after terminal status is shown. */
  dismissTimeoutId: number | null;
  /** Absolute time when terminal auto-dismiss should fire (paused while page hidden). */
  dismissDueAt: number | null;
  /** True once we observed CM accepted (code 0) before a failure. */
  sawAccepted: boolean;
}

interface FailureRow {
  reference: string;
  phoneDisplay: string;
  errorDescription: string | null;
  failedAt: string | null;
  createdAt: string;
}

const PENDING_TIMEOUT_MS = 5 * 60 * 1000;
const TERMINAL_DISMISS_MS = 45_000;

function isTerminal(status: SmsFlowStatus): boolean {
  return status === 'delivered' || status === 'failed';
}

function formatSentAt(sentAtMs: number): string {
  const sent = new Date(sentAtMs);
  const now = new Date();
  const sameDay =
    sent.getFullYear() === now.getFullYear()
    && sent.getMonth() === now.getMonth()
    && sent.getDate() === now.getDate();
  return sameDay ? sent.toLocaleTimeString() : sent.toLocaleString();
}

export function SmsPage(): HTMLElement {
  const page = document.createElement('section');
  page.className = 'module-page module-page--sms';

  page.innerHTML = `
    <section class="module-section">
      <h2 class="module-section__header">
        <span class="module-section__icon"></span>
        <span data-i18n="sms.sendTitle"></span>
      </h2>
      <p class="muted" data-i18n="sms.sendSubtitle"></p>
      <form class="form-grid" id="sms-send-form">
        <label>
          <span data-i18n="sms.phoneLabel"></span>
          <input type="tel" name="phoneNumber" required autocomplete="tel" placeholder="+31612345678" />
          <span class="muted" data-i18n="sms.phoneHint"></span>
        </label>
        <label class="message-field">
          <span data-i18n="sms.messageLabel"></span>
          <textarea class="message-input" name="message" required maxlength="640"></textarea>
        </label>
        <button type="submit" class="button primary">
          <span data-i18n="sms.sendSubmit"></span>
        </button>
      </form>
      <p class="error-text" id="sms-send-error" hidden></p>
      <p class="muted" id="sms-send-success" hidden></p>
    </section>
    <section class="module-section" id="sms-session-section" hidden>
      <h3 class="module-section__subheader" data-i18n="sms.recentTitle"></h3>
      <ul class="sms-status-list" id="sms-session-list"></ul>
    </section>
    <section class="module-section" id="sms-failures-section" hidden>
      <h3 class="module-section__subheader" data-i18n="sms.failuresTitle"></h3>
      <p class="muted" data-i18n="sms.failuresSubtitle"></p>
      <ul class="sms-status-list" id="sms-failures-list"></ul>
    </section>
  `;

  page.querySelector('.module-section__icon')?.append(iconImg('sms', 22));
  applyI18n(page);
  page.querySelector('.module-section__header')?.append(createHelpTip('sms'));

  const form = page.querySelector('#sms-send-form') as HTMLFormElement;
  const phoneInput = form.querySelector('input[name="phoneNumber"]') as HTMLInputElement;
  bindPhoneInputNormalize(phoneInput);
  const errorEl = page.querySelector('#sms-send-error') as HTMLParagraphElement;
  const successEl = page.querySelector('#sms-send-success') as HTMLParagraphElement;
  const sessionSection = page.querySelector('#sms-session-section') as HTMLElement;
  const sessionList = page.querySelector('#sms-session-list') as HTMLUListElement;
  const failuresSection = page.querySelector('#sms-failures-section') as HTMLElement;
  const failuresList = page.querySelector('#sms-failures-list') as HTMLUListElement;

  let errorKey: string | null = null;
  let successKey: string | null = null;
  let sending = false;
  let usesDevLogSink = false;
  const sessionRows = new Map<string, SessionSmsRow>();
  const failures = new Map<string, FailureRow>();

  void loadUsesDevLogSink().then((value) => {
    usesDevLogSink = value;
    renderSession();
  });

  const refreshLocale = () => {
    if (errorKey && !errorEl.hidden) {
      errorEl.textContent = t(errorKey);
    }
    if (successKey && !successEl.hidden) {
      successEl.textContent = t(successKey);
    }
    renderSession();
    renderFailures();
  };

  bindLocaleRefresh(refreshLocale);

  const hasNonTerminal = (): boolean => {
    for (const row of sessionRows.values()) {
      if (!isTerminal(row.status)) {
        return true;
      }
    }
    return false;
  };

  const syncLiveConnection = (): void => {
    if (!hasNonTerminal()) {
      disconnectSmsDeliveryLive();
      return;
    }

    if (isSmsDeliveryLiveActive()) {
      return;
    }

    connectSmsDeliveryLive({
      onUpdate: handleDeliveryUpdate,
    });
  };

  const clearDismissTimer = (row: SessionSmsRow): void => {
    if (row.dismissTimeoutId !== null) {
      window.clearTimeout(row.dismissTimeoutId);
      row.dismissTimeoutId = null;
    }
  };

  const clearRowTimers = (row: SessionSmsRow): void => {
    if (row.pendingTimeoutId !== null) {
      window.clearTimeout(row.pendingTimeoutId);
      row.pendingTimeoutId = null;
    }
    clearDismissTimer(row);
    row.dismissDueAt = null;
  };

  const removeSessionRow = (reference: string): void => {
    const row = sessionRows.get(reference);
    if (!row) {
      return;
    }
    clearRowTimers(row);
    sessionRows.delete(reference);
    renderSession();
    syncLiveConnection();
  };

  const armDismissTimer = (reference: string): void => {
    const row = sessionRows.get(reference);
    if (!row || row.dismissDueAt === null) {
      return;
    }

    clearDismissTimer(row);

    if (document.hidden) {
      return;
    }

    const remainingMs = Math.max(0, row.dismissDueAt - Date.now());
    row.dismissTimeoutId = window.setTimeout(() => {
      removeSessionRow(reference);
    }, remainingMs);
  };

  const scheduleTerminalDismiss = (reference: string): void => {
    const row = sessionRows.get(reference);
    if (!row) {
      return;
    }
    if (row.pendingTimeoutId !== null) {
      window.clearTimeout(row.pendingTimeoutId);
      row.pendingTimeoutId = null;
    }
    row.dismissDueAt = Date.now() + TERMINAL_DISMISS_MS;
    armDismissTimer(reference);
  };

  const onVisibilityChange = (): void => {
    if (document.hidden) {
      for (const row of sessionRows.values()) {
        clearDismissTimer(row);
      }
      return;
    }

    for (const row of sessionRows.values()) {
      if (row.dismissDueAt !== null && isTerminal(row.status)) {
        armDismissTimer(row.reference);
      }
    }
  };

  const handleDeliveryUpdate = (message: SmsDeliveryUpdatedMessage): void => {
    const row = sessionRows.get(message.reference);

    if (message.status === 'delivered') {
      if (row) {
        row.status = 'delivered';
        row.errorDescription = null;
        renderSession();
        scheduleTerminalDismiss(message.reference);
        syncLiveConnection();
      }
      return;
    }

    if (row) {
      if (message.status === 'accepted') {
        row.status = 'accepted';
        row.sawAccepted = true;
      } else if (message.status === 'failed') {
        row.status = 'failed';
      } else if (message.status === 'pending') {
        row.status = 'pending';
      }
      row.errorDescription = message.errorDescription;
      renderSession();
    }

    if (message.status === 'failed') {
      void (async () => {
        const phone =
          (await revealSmsDestination(message.destinationCiphertextBase64, message.reference))
          ?? row?.phone
          ?? t('sms.destinationLocked');
        failures.set(message.reference, {
          reference: message.reference,
          phoneDisplay: phone,
          errorDescription: message.errorDescription,
          failedAt: message.failedAt,
          createdAt: row ? new Date(row.sentAt).toISOString() : (message.failedAt ?? new Date().toISOString()),
        });
        if (row) {
          scheduleTerminalDismiss(message.reference);
        }
        renderFailures();
        syncLiveConnection();
      })();
    } else {
      syncLiveConnection();
    }
  };

  const renderSession = (): void => {
    sessionList.replaceChildren();
    sessionSection.hidden = sessionRows.size === 0;
    const ordered = [...sessionRows.values()].sort((a, b) => b.sentAt - a.sentAt);
    for (const item of ordered) {
      const li = document.createElement('li');
      li.className = 'sms-status-list__item';
      if (item.status === 'failed') {
        li.classList.add('sms-status-list__item--failed');
      } else if (item.status === 'delivered') {
        li.classList.add('sms-status-list__item--delivered');
      }

      const meta = document.createElement('div');
      meta.className = 'sms-status-list__meta';
      const phoneEl = document.createElement('span');
      phoneEl.className = 'sms-status-list__phone';
      phoneEl.textContent = item.phone;
      const timeEl = document.createElement('span');
      timeEl.className = 'sms-status-list__time muted';
      timeEl.textContent = formatSentAt(item.sentAt);
      meta.append(phoneEl, timeEl);

      li.append(meta, buildSmsStatusFlow(item.status, item.sawAccepted, item.errorDescription));
      if (usesDevLogSink && !isTerminal(item.status)) {
        li.append(buildSmsDevSimulateControls(item.reference));
      }
      sessionList.append(li);
    }
  };

  const renderFailures = (): void => {
    failuresList.replaceChildren();
    failuresSection.hidden = failures.size === 0;
    for (const item of failures.values()) {
      const li = document.createElement('li');
      li.className = 'sms-status-list__item sms-status-list__item--failed';

      const meta = document.createElement('div');
      meta.className = 'sms-status-list__meta';
      const phoneEl = document.createElement('span');
      phoneEl.className = 'sms-status-list__phone';
      phoneEl.textContent = item.phoneDisplay;
      const timeEl = document.createElement('span');
      timeEl.className = 'sms-status-list__time muted';
      const sentMs = Date.parse(item.createdAt);
      timeEl.textContent = Number.isFinite(sentMs) ? formatSentAt(sentMs) : item.createdAt;
      meta.append(phoneEl, timeEl);

      const flow = buildSmsStatusFlow('failed', true, item.errorDescription);

      const dismiss = document.createElement('button');
      dismiss.type = 'button';
      dismiss.className = 'button';
      dismiss.textContent = t('sms.dismissFailure');
      dismiss.addEventListener('click', () => {
        void (async () => {
          try {
            await api.dismissSmsDeliveryFailure(item.reference);
            failures.delete(item.reference);
            renderFailures();
          } catch {
            // keep row visible
          }
        })();
      });

      li.append(meta, flow, dismiss);
      failuresList.append(li);
    }
  };

  const trackSession = (reference: string, phone: string): void => {
    const pendingTimeoutId = window.setTimeout(() => {
      const row = sessionRows.get(reference);
      if (row && !isTerminal(row.status)) {
        removeSessionRow(reference);
      }
    }, PENDING_TIMEOUT_MS);

    sessionRows.set(reference, {
      reference,
      phone,
      status: 'pending',
      errorDescription: null,
      sentAt: Date.now(),
      pendingTimeoutId,
      dismissTimeoutId: null,
      dismissDueAt: null,
      sawAccepted: false,
    });
    renderSession();
    syncLiveConnection();
  };

  const loadFailures = async (): Promise<void> => {
    try {
      const response = await api.listSmsDeliveryFailures();
      failures.clear();
      for (const item of response.failures) {
        const phone =
          (await revealSmsDestination(item.destinationCiphertext, item.reference))
          ?? t('sms.destinationLocked');
        failures.set(item.reference, {
          reference: item.reference,
          phoneDisplay: phone,
          errorDescription: item.errorDescription,
          failedAt: item.failedAt,
          createdAt: item.createdAt,
        });
      }
      renderFailures();
    } catch {
      // ignore load errors on page open
    }
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (sending) {
      return;
    }

    const data = new FormData(form);
    const phoneNumber = normalizeToE164(String(data.get('phoneNumber') ?? ''));
    const message = String(data.get('message') ?? '').trim();

    errorEl.hidden = true;
    successEl.hidden = true;
    errorKey = null;
    successKey = null;

    if (!loadVaultSession()) {
      errorKey = 'sms.vaultRequired';
      errorEl.hidden = false;
      errorEl.textContent = t(errorKey);
      return;
    }

    if (!phoneNumber || !isValidE164Phone(phoneNumber)) {
      errorKey = 'sms.invalidPhone';
      errorEl.hidden = false;
      errorEl.textContent = t(errorKey);
      return;
    }

    phoneInput.value = phoneNumber;

    if (!message) {
      errorKey = 'sms.messageRequired';
      errorEl.hidden = false;
      errorEl.textContent = t(errorKey);
      return;
    }

    void (async () => {
      sending = true;
      const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
      submitButton.disabled = true;
      let trackedReference: string | null = null;
      try {
        const reference = createSmsDeliveryReference();
        const destinationCiphertext = await buildSmsDestinationCiphertext(phoneNumber, reference);
        // Open live socket before send so fast CM.com / DEBUG DLRs are not missed.
        trackSession(reference, phoneNumber);
        trackedReference = reference;
        const result = await api.sendSms({
          phoneNumber,
          message,
          reference,
          destinationCiphertext,
        });
        const trackingReference = result.reference ?? reference;
        if (trackingReference !== reference) {
          const existing = sessionRows.get(reference);
          if (existing) {
            clearRowTimers(existing);
            sessionRows.delete(reference);
            trackSession(trackingReference, phoneNumber);
            trackedReference = trackingReference;
          }
        }
        try {
          const catchUp = await catchUpSmsDeliveryStatus(trackingReference);
          handleDeliveryUpdate({
            type: 'sms.deliveryUpdated',
            reference: trackingReference,
            status: catchUp.status,
            cmStatusCode: catchUp.cmStatusCode,
            errorCode: null,
            errorDescription: catchUp.errorDescription,
            destinationCiphertextBase64: '',
            updatedAt: new Date().toISOString(),
            failedAt: catchUp.status === 'failed' ? new Date().toISOString() : null,
          });
        } catch {
          // Keep pending until live updates arrive.
        }
        successKey = 'sms.sendAccepted';
        successEl.hidden = false;
        successEl.textContent = t(successKey);
        form.reset();
      } catch (cause) {
        if (trackedReference) {
          removeSessionRow(trackedReference);
        }
        if (cause instanceof Error && cause.message === 'vault_locked') {
          errorKey = 'sms.vaultRequired';
        } else if (cause instanceof ApiError) {
          switch (cause.code) {
            case 'invalid_phone_number':
              errorKey = 'sms.invalidPhone';
              break;
            case 'destination_ciphertext_required':
            case 'destination_ciphertext_invalid':
              errorKey = 'sms.vaultRequired';
              break;
            case 'sms_not_configured':
            case 'sms_provider_error':
            case 'sms_send_failed':
              errorKey = 'sms.unavailable';
              break;
            default:
              errorKey = 'sms.sendFailed';
              break;
          }
        } else {
          errorKey = 'sms.sendFailed';
        }
        errorEl.hidden = false;
        errorEl.textContent = t(errorKey);
      } finally {
        submitButton.disabled = false;
        sending = false;
      }
    })();
  });

  void loadFailures();

  document.addEventListener('visibilitychange', onVisibilityChange);

  const observer = new MutationObserver(() => {
    if (!page.isConnected) {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      for (const row of sessionRows.values()) {
        clearRowTimers(row);
      }
      sessionRows.clear();
      disconnectSmsDeliveryLive();
      observer.disconnect();
    }
  });
  const appRoot = document.getElementById('app') ?? document.body;
  observer.observe(appRoot, { childList: true });

  return page;
}
